import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { Info } from 'lucide-react';
import { adsApi } from '@/api/client';
import { parseBrandsPayload } from '@/utils/campaignCreate';
import type { SbLandingPageKind } from '@/utils/sbAdGroupCreate';

export type SbStorePage = {
  storePageId: string;
  storePageUrl: string;
  storePageName: string;
};

export type SbStore = {
  entityId: string;
  storeId: string;
  brandEntityId: string;
  storeName: string;
  pages: SbStorePage[];
};

function asPages(raw: unknown): SbStorePage[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((page): page is Record<string, unknown> => !!page && typeof page === 'object')
    .map((page, index) => {
      const storePageUrl = String(page.storePageUrl ?? page.url ?? '').trim();
      const storePageId = String(page.storePageId ?? page.pageId ?? storePageUrl ?? `page-${index}`).trim();
      return {
        storePageId: storePageId || `page-${index}`,
        storePageUrl,
        storePageName: String(page.storePageName ?? page.name ?? 'Home').trim() || 'Home',
      };
    });
}

function parseStores(data: unknown): SbStore[] {
  const root = data && typeof data === 'object' ? (data as Record<string, unknown>) : {};
  const rows = Array.isArray(root.stores) ? root.stores : Array.isArray(data) ? data : [];
  return rows
    .filter((row): row is Record<string, unknown> => !!row && typeof row === 'object')
    .map((row) => {
      const storeId = String(row.storeId ?? row.entityId ?? row.brandEntityId ?? '').trim();
      return {
        entityId: storeId,
        storeId,
        brandEntityId: String(row.brandEntityId ?? ''),
        storeName: String(row.storeName ?? 'Brand Store'),
        pages: asPages(row.pages ?? row.storePageInfo),
      };
    })
    .filter((store) => store.entityId || store.storeName);
}

function parseStorePages(data: unknown): SbStorePage[] {
  const root = data && typeof data === 'object' ? (data as Record<string, unknown>) : {};
  return asPages(root.pages ?? root.storePages ?? data);
}

const STORE_CREATE_URL = 'https://advertising.amazon.com/help/GHTRF8Q4J4K8Z8L3';

function SearchSelect<T>({
  valueLabel,
  placeholder,
  searchPlaceholder,
  emptyLabel,
  options,
  getKey,
  getLabel,
  getHint,
  groupLabel,
  footer,
  disabled,
  onSelect,
}: {
  valueLabel: string;
  placeholder: string;
  searchPlaceholder: string;
  emptyLabel: string;
  options: T[];
  getKey: (option: T) => string;
  getLabel: (option: T) => string;
  getHint?: (option: T) => string;
  groupLabel?: string;
  footer?: string;
  disabled?: boolean;
  onSelect: (option: T) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0, width: 0 });
  const filtered = options.filter((option) =>
    getLabel(option).toLowerCase().includes(query.trim().toLowerCase())
  );

  const updatePos = () => {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) return;
    setMenuPos({ top: rect.bottom + 4, left: rect.left, width: Math.max(rect.width, 280) });
  };

  useLayoutEffect(() => {
    if (!open) return;
    updatePos();
    const onReposition = () => updatePos();
    window.addEventListener('resize', onReposition);
    document.addEventListener('scroll', onReposition, true);
    return () => {
      window.removeEventListener('resize', onReposition);
      document.removeEventListener('scroll', onReposition, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: MouseEvent) => {
      const target = event.target as Node;
      if (buttonRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    return () => document.removeEventListener('mousedown', onPointer);
  }, [open]);

  return (
    <div className="relative max-w-md">
      <button
        ref={buttonRef}
        type="button"
        disabled={disabled}
        onClick={() => setOpen((value) => !value)}
        className="flex h-10 w-full items-center justify-between rounded-md border border-border bg-surface px-3 text-left text-sm disabled:bg-page"
      >
        <span className={valueLabel ? 'text-foreground' : 'text-foreground-muted'}>
          {valueLabel || placeholder}
        </span>
        <span className="text-foreground-muted">▾</span>
      </button>
      {open && typeof document !== 'undefined'
        ? createPortal(
            <div
              ref={menuRef}
              style={{ top: menuPos.top, left: menuPos.left, width: menuPos.width }}
              className="fixed z-[80] overflow-hidden rounded-md border border-border bg-surface shadow-lg"
            >
              <input
                autoFocus
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={searchPlaceholder}
                className="h-10 w-full border-b border-border px-3 text-sm outline-none"
              />
              {groupLabel ? (
                <p className="px-3 py-2 text-xs font-medium text-foreground-muted">{groupLabel}</p>
              ) : null}
              {filtered.length === 0 ? (
                <p className="px-3 py-3 text-sm text-foreground-muted">{emptyLabel}</p>
              ) : (
                <ul className="max-h-64 overflow-auto">
                  {filtered.map((option) => (
                    <li key={getKey(option)}>
                      <button
                        type="button"
                        className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-page"
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => {
                          onSelect(option);
                          setOpen(false);
                          setQuery('');
                        }}
                      >
                        <span className="font-medium">{getLabel(option)}</span>
                        {getHint ? (
                          <span className="text-xs text-foreground-muted">{getHint(option)}</span>
                        ) : null}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {footer ? (
                <p className="border-t border-border px-3 py-2 text-xs text-foreground-muted">{footer}</p>
              ) : null}
            </div>,
            document.body
          )
        : null}
    </div>
  );
}

export function SbLandingPageField({
  profileId,
  brandEntityId,
  kind,
  onKindChange,
  storeId,
  onStoreIdChange,
  pageId,
  onPageIdChange,
  onStorePageUrlChange,
  disabled,
}: {
  profileId: string;
  brandEntityId?: string;
  kind: SbLandingPageKind;
  onKindChange: (kind: SbLandingPageKind) => void;
  storeId: string;
  onStoreIdChange: (storeId: string) => void;
  pageId: string;
  onPageIdChange: (pageId: string) => void;
  onStorePageUrlChange: (url: string) => void;
  disabled?: boolean;
}) {
  const brandsQuery = useQuery({
    queryKey: ['profile-brands', profileId],
    enabled: kind === 'STORE' && Boolean(profileId) && !brandEntityId,
    queryFn: async () => {
      const res = await adsApi.getBrands(profileId);
      if (!res.success) return [];
      return parseBrandsPayload(res.data);
    },
    staleTime: 60_000,
  });
  const rawBrandId = brandEntityId || brandsQuery.data?.[0]?.brandEntityId || '';
  const resolvedBrandId = rawBrandId.trim();
  const brandsReady = Boolean(brandEntityId) || brandsQuery.isFetched || !brandsQuery.isEnabled;
  const storesQuery = useQuery({
    queryKey: ['sb-stores', profileId, resolvedBrandId],
    enabled: kind === 'STORE' && Boolean(profileId) && brandsReady,
    queryFn: async () => {
      try {
        const res = await adsApi.listSbStores(profileId, resolvedBrandId || undefined);
        if (!res.success) return [];
        return parseStores(res.data);
      } catch {
        return [];
      }
    },
    staleTime: 60_000,
    retry: false,
  });
  const stores = storesQuery.data ?? [];
  const selectedStore = useMemo(
    () => stores.find((store) => store.entityId === storeId || store.storeId === storeId) ?? null,
    [storeId, stores]
  );
  const pagesQuery = useQuery({
    queryKey: ['sb-store-pages', profileId, storeId, selectedStore?.brandEntityId || resolvedBrandId],
    enabled: kind === 'STORE' && Boolean(profileId) && Boolean(storeId),
    queryFn: async () => {
      try {
        const res = await adsApi.listSbStorePages(
          profileId,
          storeId,
          selectedStore?.brandEntityId || resolvedBrandId || undefined
        );
        if (!res.success) return selectedStore?.pages ?? [];
        const loaded = parseStorePages(res.data);
        return loaded.length ? loaded : selectedStore?.pages ?? [];
      } catch {
        return selectedStore?.pages ?? [];
      }
    },
    staleTime: 60_000,
    retry: false,
  });
  const pages = pagesQuery.data ?? selectedStore?.pages ?? [];
  const selectedPage = pages.find((page) => page.storePageId === pageId) ?? null;

  useEffect(() => {
    if (kind !== 'STORE') {
      onStorePageUrlChange('');
      return;
    }
    if (stores.length === 0) {
      onStorePageUrlChange('');
      return;
    }
    const store = stores.find((item) => item.entityId === storeId || item.storeId === storeId);
    if (!storeId) {
      onStoreIdChange(stores[0].entityId);
      onPageIdChange('');
      onStorePageUrlChange('');
      return;
    }
    if (!store) return;
    if (!pages.length) {
      if (!pagesQuery.isFetching) onStorePageUrlChange('');
      return;
    }
    const page = pages.find((item) => item.storePageId === pageId) ?? (pageId ? null : pages[0]);
    if (!pageId && page) {
      onPageIdChange(page.storePageId);
      onStorePageUrlChange(page.storePageUrl);
      return;
    }
    onStorePageUrlChange(page?.storePageUrl ?? '');
  }, [
    kind,
    onPageIdChange,
    onStoreIdChange,
    onStorePageUrlChange,
    pageId,
    pages,
    pagesQuery.isFetching,
    storeId,
    stores,
  ]);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-1.5">
        <p className="text-sm font-semibold text-foreground">Landing page</p>
        <Info className="h-3.5 w-3.5 text-foreground-subtle" aria-hidden />
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="radio"
          name="sb-landing-page"
          checked={kind === 'PRODUCT_LIST'}
          onChange={() => onKindChange('PRODUCT_LIST')}
          disabled={disabled}
        />
        New landing page
      </label>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="radio"
          name="sb-landing-page"
          checked={kind === 'STORE'}
          onChange={() => onKindChange('STORE')}
          disabled={disabled}
        />
        Brand Store on Amazon
      </label>

      {kind === 'STORE' ? (
        <div className="space-y-3 pl-6">
          {storesQuery.isPending ? (
            <p className="text-sm text-foreground-muted">Loading stores…</p>
          ) : storesQuery.isError ? (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-950">
              <p className="font-medium">Couldn’t load Brand Stores from Amazon.</p>
              <button
                type="button"
                className="mt-1 text-brand hover:underline"
                onClick={() => void storesQuery.refetch()}
              >
                Try again
              </button>
            </div>
          ) : stores.length === 0 ? (
            <div className="rounded-md border border-sky-200 bg-sky-50 px-3 py-3 text-sm text-sky-950">
              <p className="font-medium">No Stores in your account. Create a store for your brand on Amazon for free.</p>
              <a href={STORE_CREATE_URL} target="_blank" rel="noreferrer" className="mt-1 inline-flex text-brand hover:underline">
                Learn how
              </a>
            </div>
          ) : null}
          <div>
            <p className="mb-1 text-sm font-medium">Choose a Brand Store</p>
            <SearchSelect
              valueLabel={selectedStore?.storeName ?? ''}
              placeholder="Choose a Brand Store"
              searchPlaceholder="Search for a Store on Amazon"
              emptyLabel="No stores match that search."
              options={stores}
              getKey={(store) => store.entityId}
              getLabel={(store) => store.storeName}
              groupLabel={stores.length ? `All Stores (${stores.length})` : undefined}
              footer="You can search for a Store on Amazon that is not associated with your account."
              disabled={disabled || stores.length === 0}
              onSelect={(store) => {
                onStoreIdChange(store.entityId);
                onPageIdChange('');
                onStorePageUrlChange('');
              }}
            />
          </div>
          <div>
            <p className="mb-1 text-sm font-medium">Choose a page</p>
            {storeId && pagesQuery.isPending ? (
              <p className="mb-2 text-sm text-foreground-muted">Loading pages…</p>
            ) : null}
            <div className="flex flex-wrap items-center gap-3">
              <SearchSelect
                valueLabel={selectedPage?.storePageName ?? ''}
                placeholder="Choose a page"
                searchPlaceholder={`Search for a page in ${selectedStore?.storeName ?? 'this store'}`}
                emptyLabel="No pages match that search."
                options={pages}
                getKey={(page) => page.storePageId}
                getLabel={(page) => page.storePageName}
                groupLabel={
                  selectedStore ? `${selectedStore.storeName} pages (${pages.length})` : undefined
                }
                disabled={disabled || pages.length === 0}
                onSelect={(page) => {
                  onPageIdChange(page.storePageId);
                  onStorePageUrlChange(page.storePageUrl);
                }}
              />
              {selectedPage?.storePageUrl ? (
                <a
                  href={selectedPage.storePageUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm text-brand hover:underline"
                >
                  See page
                </a>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
